import { useState } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import {
  Card,
  Grid,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import MDBox from "../../../../components/MDBox";
import MDTypography from "../../../../components/MDTypography";
import MDButton from "../../../../components/MDButton";
import MDInput from "../../../../components/MDInput";
import profileService from "../../../../services/profileService";

// Esquema de validación robusto
const passwordSchema = yup.object().shape({
  currentPassword: yup.string().required("La contraseña actual es obligatoria"),

  newPassword: yup
    .string()
    .required("La nueva contraseña es obligatoria")
    .min(8, "Mínimo 8 caracteres")
    .matches(/[0-9]/, "Debe contener al menos un número")
    .matches(/[!@#$%^&*(),.?":{}|<>]/, "Debe contener al menos un carácter especial")
    .matches(/[A-Z]/, "Debe contener al menos una mayúscula")
    .matches(/[a-z]/, "Debe contener al menos una minúscula"),

  confirmPassword: yup
    .string()
    .required("Debes confirmar la nueva contraseña")
    .oneOf([yup.ref("newPassword")], "Las contraseñas no coinciden"),
});

function ChangePassword({ id }) {
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(passwordSchema),
    mode: "onChange",
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data) => {
    setLoading(true);
    setSuccess(false);

    try {
      await profileService.updatePassword(id, data);

      setSuccess(true);
      reset();
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      const message = err.response?.data?.error || "Error al cambiar la contraseña";
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const passwordRequirements = [
    "Al menos 8 caracteres",
    "Una mayúscula y una minúscula",
    "Al menos un número",
    "Al menos un carácter especial (!@#$%&*)",
  ];

  return (
    <Card id="change-password">
      <MDBox p={3}>
        <MDTypography variant="h5" fontWeight="medium">
          Cambiar contraseña
        </MDTypography>
      </MDBox>

      <MDBox component="form" pb={3} px={3} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Contraseña actual */}
          <Grid item xs={12}>
            <Controller
              name="currentPassword"
              control={control}
              render={({ field }) => (
                <MDInput
                  {...field}
                  fullWidth
                  type={showCurrent ? "text" : "password"}
                  label="Contraseña actual"
                  error={!!errors.currentPassword}
                  helperText={errors.currentPassword?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowCurrent(!showCurrent)}
                          edge="end"
                        >
                          {showCurrent ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Nueva contraseña */}
          <Grid item xs={12} sm={12}>
            <Controller
              name="newPassword"
              control={control}
              render={({ field }) => (
                <MDInput
                  {...field}
                  fullWidth
                  type={showNew ? "text" : "password"}
                  label="Nueva contraseña"
                  error={!!errors.newPassword}
                  helperText={errors.newPassword?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowNew(!showNew)} edge="end">
                          {showNew ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Confirmar contraseña */}
          <Grid item xs={12} sm={12}>
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <MDInput
                  {...field}
                  fullWidth
                  type={showConfirm ? "text" : "password"}
                  label="Confirmar nueva contraseña"
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">
                          {showConfirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Requisitos */}
        <MDBox mt={4}>
          <MDTypography variant="h6" fontWeight="medium" mb={1}>
            Requisitos de seguridad
          </MDTypography>
          <MDBox component="ul" pl={3} m={0}>
            {passwordRequirements.map((req, i) => (
              <MDBox key={i} component="li" color="text" fontSize="0.875rem" lineHeight={1.5}>
                <MDTypography variant="button" color="text" fontWeight="regular">
                  {req}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>
        </MDBox>

        {/* Mensaje de éxito */}
        {success && (
          <MDBox mt={2} p={2} bgColor="success" color="white" borderRadius="md">
            <MDTypography variant="body2" color="white" fontWeight="medium">
              Contraseña actualizada correctamente
            </MDTypography>
          </MDBox>
        )}

        {/* Botón */}
        <MDBox mt={4} textAlign="right">
          <MDButton
            variant="gradient"
            color="info"
            type="submit"
            disabled={loading}
          >
            {loading ? "Actualizando..." : "Actualizar contraseña"}
          </MDButton>
        </MDBox>
      </MDBox>
    </Card>
  );
}

ChangePassword.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default ChangePassword;